import type { ProductPageData } from "components/ProductPageContent";
import graniteCatalogue from "assets/ming-stone-granite.pdf";
import limestoneMarbleCatalogue from "assets/ming-stone-limestone-marble.pdf";
import sandstoneCatalogue from "assets/ming-stone-sandstone.pdf";
import pebbleMosaicCatalogue from "assets/ming-stone-pebble-mosaic.pdf";
import volcanicRockCatalogue from "assets/ming-stone-volcanic-rock.pdf";
import brickMosaicCatalogue from "assets/ming-stone-brick-mosaic.pdf";
import carvedAnimalsCatalogue from "assets/ming-stone-carved-animals.pdf";
import carvedFountainsCatalogue from "assets/ming-stone-carved-fountains.pdf";
import carvedLanternsCatalogue from "assets/ming-stone-carved-lanterns.pdf";
import flexibleStoneVeneerCatalogue from "assets/ming-stone-flexible-stone-veneer.pdf";

export const PRODUCT_PAGES: Record<string, ProductPageData> = {
  granite: {
    cataloguePdf: graniteCatalogue,
    title: "Granite — Wholesale Landscape Stone",
    intro:
      "Wholesale granite for landscaping distributors. Supplied from China and other origins, direct to your designated location, cut to size to your design requirements. We process and deliver to meet our clients' requirements.",
    sections: [
      {
        heading: "Landscape product types",
        bullets: [
          "Paving",
          "Kerb",
          "Tread / Step",
          "Cobble (including special shapes and mixed colours)",
          "Blind / Tactile Stone (including Corduroy / Blister)",
          "Wall Stone / Cladding / Stacked Panel",
          "Coping, Window Sill, and Pillar Cap",
          "Columns / Pillars and Bollards",
          "Benches and Planters / Flower Pots",
          "Water Groove",
          "Walkway and Driveway Entrance Stones",
          "Play Stone",
        ],
      },
      {
        heading: "Colours — how to enquire",
        paragraphs: [
          "Our granite colours are listed by code in the Granite Catalogue. Please enquire by code and finish. Do not assume every colour is available in every finish — we will confirm per code.",
        ],
        codes: [
          "MSG38",
          "MSG39",
          "MSG301X",
          "MSG337",
          "MSG595",
          "MSG654H",
          "MSGC00",
          "MSGC43",
          "MSGC50",
          "MSGC52",
          "MSGC55",
          "MSGC59",
          "MSGC75",
          "MSGC86",
          "MSGC98B",
        ],
      },
      {
        heading: "Wall stone",
        paragraphs: [
          "Granite wall stone is also available. See our central Wall Stone & Ledge Panel page, organised by colour — customers choose the colour and the shape is made to order.",
        ],
      },
    ],
    enquiry: [
      "Company name and country / state / province",
      "Business type: landscaping wholesaler / distributor / other B2B",
      "Product type and product code(s) and colour",
      "Processing / finish required, or a reference photo of the desired finish or colour",
      "Size / thickness and cut-to-size requirements",
      "Quantity (sqm / pieces / containers)",
      "Delivery location / destination port and desired delivery time",
    ],
  },
  "limestone-marble": {
    cataloguePdf: limestoneMarbleCatalogue,
    title: "Limestone & Marble — Wholesale Stone",
    intro:
      "Wholesale limestone and marble for landscaping distributors. Supplied from China and other origins, direct to your designated location, cut to size to your design requirements. We process and deliver to meet our clients' requirements.",
    sections: [
      {
        heading: "Limestone product types",
        bullets: [
          "Tiles",
          "Paving",
          "Tread",
          "Cobble",
          "Wall Stone (= Wall Cladding)",
          "Coping",
          "Ledge Panel",
          "Stacked Panel",
          "Corner",
          "Table",
          "Pillar Cap",
        ],
      },
      {
        heading: "Limestone colours and varieties",
        paragraphs: [
          "All limestone varieties below can be made into: Paving, Cobble, Corner, Wall Stone, Tread, Table, Coping, and Pillar Cap. Sizes and thickness are made to order, according to each customer's requirements. Grooved and Bullnose edge treatments are also available where applicable.",
        ],
        bullets: [
          "Blue Limestone",
          "Brown Limestone",
          "Cinderella",
          "Black Limestone",
          "Buff Limestone",
          "Grey Limestone Slab — Veincut and Crosscut slabs",
        ],
      },
      {
        heading: "Marble",
        paragraphs: [
          "Green Marble Slab — shown as Green Marble-1 through Green Marble-6 in the catalogue, also known as Ming Green. Green Marble Slab (= Ming Green) is the same variety, not a separate one.",
          "Additional marble photos, not yet confirmed as a separate catalogue variety — please enquire: White Marble (slabs with bullnose edge, tiles / slabs stacked, and large slabs laid out); Purple White (tiles, white with purple / grey veins); Sand River (tiles and slabs, beige).",
          "Marble finishes are confirmed per enquiry: we process to meet clients' finish requirements. If you need a specific finish, send us a photo and we will have the factory process it to match.",
        ],
      },
      {
        heading: "Wall stone",
        paragraphs: [
          "Limestone wall stone is also available. See our central Wall Stone & Ledge Panel page, organised by colour — customers choose the colour and the shape is made to order.",
        ],
      },
    ],
    enquiry: [
      "Company name and country / state / province",
      "Product: Limestone / Marble, and product type detail",
      "Colour / variety and product code, if known",
      "Processing / finish required, or a reference photo",
      "Size / thickness and cut-to-size requirements",
      "Quantity (sqm / pieces / containers)",
      "Delivery location / destination port and desired delivery time",
    ],
  },
  sandstone: {
    cataloguePdf: sandstoneCatalogue,
    title: "Sandstone — Wholesale Landscape Stone",
    intro:
      "Wholesale sandstone for landscaping distributors. Supplied from China and other origins, direct to your designated location, cut to size to your design requirements. We process and deliver to meet our clients' requirements.",
    sections: [
      {
        heading: "Sandstone product types",
        paragraphs: [
          "Please confirm the product type for each colour in your enquiry; final confirmation is per colour and finish.",
        ],
        bullets: [
          "Paving",
          "Tread",
          "Cobble",
          "Wall Cladding",
          "Coping",
          "Window Sill",
          "Ledge Panel",
          "Corner",
          "Panel",
          "Slab",
          "Kerb",
        ],
      },
      {
        heading: "Sandstone colours",
        paragraphs: [
          "Please enquire by colour name. Sizes and thickness are made to order, according to each customer's requirements.",
        ],
        bullets: [
          "Yellow Sandstone A",
          "Yellow Sandstone B",
          "Yellow Vein Sandstone",
          "York Sandstone",
          "Grey Green Sandstone",
          "Grey Green Vein Sandstone",
          "Grey White Sandstone",
          "Grey Sandstone",
          "Blue Sandstone",
          "Dark Black Sandstone",
          "Purple Sandstone A",
          "Purple Sandstone B",
          "Red Sandstone",
          "White Sandstone A",
          "White Sandstone B",
        ],
      },
      {
        heading: "Wall stone",
        paragraphs: [
          "Sandstone wall stone is also available. See our central Wall Stone & Ledge Panel page, organised by colour — customers choose the colour and the shape is made to order.",
        ],
      },
    ],
    enquiry: [
      "Company name and country / state / province",
      "Product type detail and colour name",
      "Processing / finish required, or a reference photo",
      "Size / thickness and cut-to-size requirements",
      "Quantity (sqm / pieces / containers)",
      "Delivery location / destination port and desired delivery time",
    ],
  },
  "pebble-mosaic": {
    cataloguePdf: pebbleMosaicCatalogue,
    title: "Pebble & Mosaic — Wholesale Natural Stone",
    intro:
      "Wholesale pebble and pebble mosaic for landscaping distributors. Supplied from China and other origins, direct to your designated location. We process and deliver to meet our clients' requirements.",
    sections: [
      {
        heading: "Polished Pebble",
        paragraphs: ["Polished pebble, loose."],
        bullets: [
          "Sizes: Φ 10-30mm, 20-50mm, 50-80mm",
          "Colours: Black, White, Red, Yellow, Mix Color, All Color, Zebra",
        ],
      },
      {
        heading: "Pebble Mosaic",
        bullets: [
          "Vertical Pebble",
          "Flat Cut Pebble",
          "Customised Pebble",
        ],
        paragraphs: [
          "Mosaic patterns are shown by photo, without named patterns. Please enquire by photo, so we can confirm the exact pattern, colour, and sheet size. Packing for mosaic is shown in the catalogue; please confirm packing requirements in your enquiry.",
          "Installed example: a pebble garden wall (vertical pebble). A pebble mosaic of round, multi-colour pebbles on a sheet is also shown in this range.",
        ],
      },
      {
        heading: "Gravel & Tumbled Pebble",
        paragraphs: [
          "Gravel and tumbled pebble, loose. Colours and mixes are shown by photo. Please enquire by photo and size.",
        ],
        bullets: [
          "Sizes: Φ 1-3mm, 3-5mm, 5-8mm, 8-10mm, 10-20mm, 20-30mm, 30-50mm, 50-80mm",
        ],
      },
      {
        heading: "Finishes",
        paragraphs: [
          "Pebble and mosaic in this range are Polished / Tumbled. We process to meet clients' finish requirements.",
        ],
      },
    ],
    enquiry: [
      "Company name and country / state / province",
      "Product type: Polished Pebble / Pebble Mosaic (Vertical / Flat Cut / Customised) / Gravel & Tumbled Pebble",
      "Colour and size, or a reference photo of the desired pattern or colour",
      "Sheet size for mosaic, if required",
      "Quantity (sqm / pieces / containers / tonnes, as applicable)",
      "Delivery location / destination port and desired delivery time",
    ],
  },
  "volcanic-rock": {
    cataloguePdf: volcanicRockCatalogue,
    title: "Volcanic Rock — Wholesale Landscape Stone",
    intro:
      "Wholesale volcanic rock for landscaping distributors. Supplied from China and other origins, direct to your designated location, cut to size to your design requirements. We process and deliver to meet our clients' requirements.",
    sections: [
      {
        heading: "Volcanic rock product types",
        bullets: ["Paving", "Cobble", "Wall Cladding", "Ledge Panel"],
      },
      {
        heading: "Volcanic rock colours",
        bullets: ["Black Volcanic Rock", "Red Volcanic Rock"],
      },
      {
        heading: "Finishes",
        paragraphs: [
          "Finishes in this range: all sides sawn or natural sides (random). Sizes and thickness are made to order, according to each customer's requirements.",
        ],
      },
    ],
    enquiry: [
      "Company name and country / state / province",
      "Product type and colour: Black / Red Volcanic Rock",
      "Finish: All sides sawn / Natural sides (random) / Other, or a reference photo",
      "Size / thickness and cut-to-size requirements",
      "Quantity (sqm / pieces / containers)",
      "Delivery location / destination port and desired delivery time",
    ],
  },
};

PRODUCT_PAGES["wall-stone"] = {
  title: "Wall Stone & Ledge Panel — Veneer Stone",
  intro:
    "Wholesale wall stone and ledge panel / veneer stone for landscaping distributors. Supplied from China and other origins, direct to your designated location, cut to size to your design requirements. We process and deliver to meet our clients' requirements. Base stones in this catalogue include: Slate, Quartz, Granite, Marble, Limestone, and Sandstone.",
  sections: [
    {
      heading: "How to choose — colour first, shape to order",
      paragraphs: [
        "For all wall stone, shapes can be made to different requirements. Customers only need to choose the colour; the shape is made to order accordingly. The photos show colours, not fixed shapes. Wall stone is usually natural face.",
      ],
    },
    {
      heading: "Wall stone and veneer forms",
      bullets: [
        "Random Wall Stone",
        "Strip Wall Stone",
        "Square Wall Stone",
        "Panel / Flat Panel / 3D Panel",
        "L Corner / L Corner Panel",
        "Split Ledge Stone / Ledge Panel",
        "Flag Stone",
        "Stepping Stone",
        "Tumbled Random Stone",
        "Tiles",
        "Meshed Back Paving / Meshed Stone",
        "Castle Stone",
        "Mushroom Stone",
        "Grooved Panel",
        "Coping",
        "Treads",
        "Stacked Panel",
        "Cement Back Stone Panels",
      ],
    },
    {
      heading: "Veneer formats",
      bullets: [
        "Random Veneer — R001 to R007",
        "Square Veneer — S001 to S007",
        "Loose Veneer — shown by colour and photo",
        "Meshed Stone — MS1, MS2, MS3, MS4, MS5 series, and MSLP002 to MSLP005 series",
        "Ledge Panel — including Cinderella Ledge Panel 1 to 7, and other ledge panels shown by photo",
      ],
      paragraphs: [
        "Please enquire by form and by series / colour name or photo, so we can confirm the exact product.",
      ],
    },
    {
      heading: "Colours and series",
      paragraphs: [
        "Representative names from this catalogue include the colours below. Many other colours and series are shown by photo and code only — please enquire by photo or code.",
      ],
      bullets: [
        "Celtic Blend, MSP014, MSS20, Grey Quartz, Sunset, Tobacco, Tuscan Sun, Silver",
        "White Quartz, Flamed White Quartz, White Sandstone, Grey White Sandstone, Black Slate, Flamed Black Quartz",
        "Blue Limestone, Black Limestone, Golden Black, Golden White, Golden Wheat, Rusty Quartz, Golden Granite",
        "Buff Limestone, Antique Brown, Buff Sandstone, Red Sandstone, Alaska Grey, Jet Black, Grey White",
        "Oriental Grey, Oriental White, White Marble, Cinderella, Alaska Grey Marble, Golden Vein Marble",
        "Sunset Silver, Arctic Gold, Black Quartz, Silver Black, Dragon Black, Rainbow Sandstone",
        "Grey Green Sandstone, Blue Sandstone, MSP013, Golden Line, Jade Grey, Green Slate, Silver Grey",
        "Light Beige Limestone, Champagne Grey Limestone, Blue Vein Limestone, Vals Green Granite",
        "Jurassic Granite, Devon Slate, Champagne Quartz, Alpine Blend, Carbon",
      ],
    },
    {
      heading: "Colour examples — shown by photo",
      paragraphs: [
        "The following colours are shown by photo in this range; please enquire by colour name and photo. Shapes in photos are examples only.",
      ],
      bullets: [
        "Random wall stone in dark grey, warm brown, light beige grey, tan, black, rust brown, light grey tan, yellow brown, and grey white mix",
        "Warm Beige (H-1) and Grey White (H-7), White, Grey White, and Grey wall stone in square / rectangular pieces",
        "Coffee Stone and Coffee Limestone",
        "Rustic Granite — same colour, different shapes, made to order: Random, Rectangular, Panel, and installed examples",
        "Black Quartz, Blue Quartz, Carbon, Celtic Blend, Flamed Black Quartz, Random White Sandstone, White Quartz, White Sandstone",
        "Jet Black, Lemon, Tobacco (including tumbled cobble paving in the same Tobacco colour), Tuscan Sun, Silver",
        "ZSJ colours, 013 Quartz, Buff Sandstone, Canyon Quartz, Grey Marble, Mixture Color, Rainbow Sandstone, Running River Granite, Rusty Quartz",
        "Black Limestone, Buff Limestone (A and B kept as separate codes), Grey Limestone",
        "Grey tumbled wall stone, Sheffield Grey Tumbled Veneer, MSGL001 Wall Stone, grey veined panel, and dark grey, light beige, and mid grey panels",
        "HBY wall stone (HBY-1, HBY-4, HBY-5, HBY-6) — HBY, ZSJ and N20 are shown as codes only",
        "Random Black Granite — shown laid out as large irregular pieces; please confirm in enquiry whether this colour is required as wall stone or paving",
      ],
    },
    {
      heading: "Ledge Panel — clear photos",
      bullets: [
        "Alaska Grey Marble, Arctic White, Black Slate (shown as separate entries), Black Quartz, Golden Vein Marble, Golden White, N20 (corner and flat panel)",
        "White Wood Grain / Oak White Ledgestone — one colour, different forms, in this order: 3D wall, Split wall, L corners (YLC001-L, YLC003-3DL), then sample panels (flat and 3D)",
      ],
    },
    {
      heading: "Grey Green Slate (SGZ Slate) — one mine, different products",
      paragraphs: [
        "Same mine slate, processed into different products. Colour varies naturally from dark grey to light grey green — please enquire by photo.",
      ],
      bullets: [
        "Wall Stone, Panel / Tile (stacked), Large Slab / Paving, Random / Stepping Stone",
        "Cobble Paving (installed), Thick Block (stacked)",
        "Fine Picked (= Bush Hammered) Panel and Fine Picked (= Bush Hammered) Paving (installed)",
      ],
    },
  ],
  orderingNote:
    "Sizes and thickness are made to order, according to each customer's requirements.",
  enquiry: [
    "Company name and country / state / province",
    "Product form: Random / Strip / Square / Panel / L Corner / Ledge Panel / Meshed Stone / Castle Stone / Mushroom Stone / Cement Back Panel / Other",
    "Series / colour name or code, if known, or a reference photo",
    "Size / thickness and cut-to-size requirements",
    "Quantity (sqm / pieces / containers)",
    "Delivery location / destination port and desired delivery time",
  ],
};

PRODUCT_PAGES["brick-mosaic"] = {
  cataloguePdf: brickMosaicCatalogue,
  title: "Brick Mosaic — Wholesale Natural Stone Mosaic",
  intro:
    "Wholesale brick mosaic for landscaping and wall distributors. Supplied from China and other origins, direct to your designated location, cut to size to your design requirements. We process and deliver to meet our clients' requirements. Brick mosaic is shown here as brick-shaped stone pieces in mosaic sheets / panels, for wall cladding and decorative wall use. Please enquire by code and photo.",
  sections: [
    {
      heading: "Brick Mosaic codes",
      paragraphs: [
        "Colours and patterns are shown by photo and code only. No colour names are invented here — please enquire by MSBM code and reference photo. Sizes, sheet size, thickness, and packing are not stated in the catalogue. Please confirm them in your enquiry; sizes are made to order according to each customer's requirements where applicable. The catalogue also includes unnumbered installation / close-up photo pages — please enquire by photo for those.",
      ],
      codes: Array.from({ length: 34 }, (_, i) => `MSBM-${i + 1}`),
    },
  ],
  enquiry: [
    "Company name and country / state / province",
    "MSBM code, if known, and a reference photo",
    "Size / sheet size / thickness requirements",
    "Quantity (sqm / sheets / containers)",
    "Delivery location / destination port and desired delivery time",
  ],
};

PRODUCT_PAGES["carved-animals"] = {
  cataloguePdf: carvedAnimalsCatalogue,
  title: "Carved Animals — Wholesale Natural Stone Carvings",
  intro:
    "Wholesale carved stone animals for landscaping distributors. Supplied from China and other origins, direct to your designated location, made to your design requirements where applicable. We process and deliver to meet our clients' requirements. Carved animals are shown here by photo and code only. Please enquire by code and reference photo.",
  sections: [
    {
      heading: "Carved Animal codes",
      paragraphs: [
        "Animal types, stone types, sizes, weights, and finishes are not stated in the catalogue. No animal names are invented here — please enquire by CA code and photo, and confirm stone type, size, and finish in your enquiry.",
      ],
      codes: Array.from({ length: 43 }, (_, i) => `CA${i + 1}`),
    },
    {
      heading: "Custom designs",
      paragraphs: [
        "If you have a design, drawing, or reference photo, send it with your enquiry and confirm feasibility, stone type, size, and price in advance. We process to meet clients' requirements.",
      ],
    },
  ],
  orderingNote:
    "For carved pieces, please confirm the minimum order in your enquiry.",
  enquiry: [
    "Company name and country / state / province",
    "CA code, if known, and a reference photo",
    "Stone type, if required, and size / weight requirements",
    "Quantity (pieces / containers)",
    "Delivery location / destination port and desired delivery time",
  ],
};

PRODUCT_PAGES["carved-fountains"] = {
  cataloguePdf: carvedFountainsCatalogue,
  title: "Carved Fountains — Wholesale Natural Stone Fountains",
  intro:
    "Wholesale carved stone fountains for landscaping distributors. Supplied from China and other origins, direct to your designated location, made to your design requirements where applicable. We process and deliver to meet our clients' requirements. Carved fountains are shown here by photo and code only. Please enquire by code and reference photo.",
  sections: [
    {
      heading: "Carved Fountain codes",
      paragraphs: [
        "Fountain styles, stone types, sizes, tiers, weights, and finishes are not stated in the catalogue. No style names are invented here — please enquire by CF code and photo, and confirm stone type, size, and finish in your enquiry.",
        "Fountain accessories, pumps, plumbing, and water systems are not stated in the catalogue. Please confirm in your enquiry what is included and what is supplied separately. Do not assume a pump or plumbing is included.",
      ],
      codes: Array.from({ length: 170 }, (_, i) => `CF${i + 1}`),
    },
    {
      heading: "Custom designs",
      paragraphs: [
        "If you have a design, drawing, or reference photo, send it with your enquiry and confirm feasibility, stone type, size, and price in advance. We process to meet clients' requirements.",
      ],
    },
  ],
  orderingNote:
    "For carved pieces, please confirm the minimum order in your enquiry.",
  enquiry: [
    "Company name and country / state / province",
    "CF code, if known, and a reference photo",
    "Stone type, size / tiers / weight requirements",
    "Accessories required: pump / plumbing / water system — please specify",
    "Quantity (pieces / containers)",
    "Delivery location / destination port and desired delivery time",
  ],
};

PRODUCT_PAGES["carved-lanterns"] = {
  cataloguePdf: carvedLanternsCatalogue,
  title: "Carved Lanterns — Wholesale Natural Stone Lanterns",
  intro:
    "Wholesale carved stone lanterns for landscaping distributors. Supplied from China and other origins, direct to your designated location, made to your design requirements where applicable. We process and deliver to meet our clients' requirements. Carved lanterns are shown here by photo and code only. Please enquire by code and reference photo.",
  sections: [
    {
      heading: "Carved Lantern codes",
      paragraphs: [
        "Lantern styles, stone types, sizes, weights, and finishes are not stated in the catalogue. No style names are invented here — please enquire by CL code and photo, and confirm stone type, size, and finish in your enquiry.",
        "Lighting, lamps, wiring, and electrical parts are not stated in the catalogue. Please confirm in your enquiry what is included and what is supplied separately. Do not assume lighting is included, except where a code is explicitly marked -Light / -L, and even then confirm the lamp type in enquiry.",
      ],
      codes: [
        ...Array.from({ length: 113 }, (_, i) => `CL${i + 1}`),
        "CL87-Light",
        "CL88-L",
        "CL89-L",
        "CL90-L",
        "CL91-L",
        "CL92-L",
      ],
    },
    {
      heading: "Custom designs",
      paragraphs: [
        "If you have a design, drawing, or reference photo, send it with your enquiry and confirm feasibility, stone type, size, and price in advance. We process to meet clients' requirements.",
      ],
    },
  ],
  orderingNote:
    "For carved pieces, please confirm the minimum order in your enquiry.",
  enquiry: [
    "Company name and country / state / province",
    "CL code, if known, including -Light / -L version if required, and a reference photo",
    "Stone type, size / weight requirements",
    "Lighting required: yes / no — please specify lamp type if yes",
    "Quantity (pieces / containers)",
    "Delivery location / destination port and desired delivery time",
  ],
};

PRODUCT_PAGES["new-carvings"] = {
  title: "New Carvings — Wholesale Natural Stone Carvings",
  intro:
    "Wholesale new stone carvings for landscaping distributors. Supplied from China and other origins, direct to your designated location, made to your design requirements where applicable. We process and deliver to meet our clients' requirements. New carvings are shown here by photo and code / name only, as listed in this catalogue. Please enquire by the exact name and number below, and reference photo. This range is kept separate from the CA, CF, and CL code ranges — the numbering is different and the ranges are not merged.",
  sections: [
    {
      heading: "New Carvings",
      paragraphs: [
        "Stone types, sizes, weights, finishes, and — for fountains and lanterns — pumps, plumbing, lamps, and wiring are not stated in this catalogue. Confirm all of these in enquiry. Do not assume a pump, plumbing, or lamp is included. The meanings of CD, CPX, CK, and CHP are not stated in this catalogue and are not invented here.",
      ],
      codes: [
        ...Array.from({ length: 8 }, (_, i) => `Lantern ${i + 1}`),
        ...Array.from({ length: 10 }, (_, i) => `Fountain ${i + 1}`),
        ...Array.from({ length: 9 }, (_, i) => `Elephant ${i + 1}`),
        ...Array.from({ length: 10 }, (_, i) => `CD ${i + 1}`),
        ...Array.from({ length: 5 }, (_, i) => `CPX ${i + 1}`),
        ...Array.from({ length: 5 }, (_, i) => `CK ${i + 1}`),
        ...Array.from({ length: 6 }, (_, i) => `Lion ${i + 1}`),
        ...Array.from({ length: 10 }, (_, i) => `CHP ${i + 1}`),
      ],
    },
    {
      heading: "Custom designs",
      paragraphs: [
        "If you have a design, drawing, or reference photo, send it with your enquiry and confirm feasibility, stone type, size, and price in advance. We process to meet clients' requirements.",
      ],
    },
  ],
  orderingNote:
    "For carved pieces, please confirm the minimum order in your enquiry.",
  enquiry: [
    "Company name and country / state / province",
    "Product: New Carving — Lantern / Fountain / Elephant / CD / CPX / CK / Lion / CHP, and exact code / number",
    "Reference photo, stone type, and size / weight requirements",
    "Accessories required: pump / plumbing / lamp — please specify",
    "Quantity (pieces / containers)",
    "Delivery location / destination port and desired delivery time",
  ],
};

PRODUCT_PAGES["flexible-stone-veneer"] = {
  cataloguePdf: flexibleStoneVeneerCatalogue,
  hideColourNote: true,
  hideProcessingNote: true,
  title: "Flexible Stone Veneer (Soft Stone) — Wholesale Flexible Wall Veneer",
  intro: "Wholesale flexible stone veneer for landscaping and building distributors. A lightweight, flexible wall veneer made of modified clay (MCM) — neither real stone nor ceramic tile — with a stone look, for indoor and outdoor wall use. Supplied from China and other origins, direct to your designated location. We process and deliver to meet our clients' requirements. This is a separate product range from our natural stone pages. Flexible stone veneer is shown here by photo and code only. Please enquire by catalogue number or model code and reference photo. Colours shown on the sample sheets are examples, not a fixed range — the source sheets state: Not limited to the following colors.",
  sections: [
    { heading: "Colour matching", paragraphs: ["Flexible stone veneer can also be produced in the colours of our natural stone range. Please email us to discuss your specific colour requirements."] },
    { heading: "Travertine", paragraphs: ["Catalogue 1 Travertine: regular size 600x1200 mm, 1200x3000 mm; thickness 2-3 mm. Example colours: Light grey, Beige White, Beige yellow, Dark grey.", "Catalogue 4 Travertine: legible codes DS-001, DS-005, DS-006, DS-008, DS-009, DS-010. Size and thickness not legible on this sheet — confirm in enquiry.", "Catalogue 5-6 Travertine: legible codes DS-012, DS-013, DS-015, DS-017, DS-018, DS-021. Other DS numbers do not appear in these files.", "Catalogue 27 Mosaic Travertine: 1200x600 mm, 1200x3000 mm; thickness 2.5-8 mm. Example colours: Khaki, Beige White, Light grey, Black, Beige yellow."] },
    { heading: "Travertine Printing series — enquire by code and photo only", paragraphs: ["No size, thickness, weight or performance specification is legible on these pages. Names repeat across codes, so the code, not the name, is the identifier. Please enquire by YES code and photo, and confirm size, thickness and packing in enquiry."], codes: ["YES101 Italian Travertine", "YES102 Light Beige Travertine", "YES103 Beige Yellow Travertine", "YES104 Beige Yellow Travertine", "YES105 Silver Grey Travertine", "YES106 Roman Yellow Travertine", "YES107 Beige Yellow Travertine", "YES108 Beige Yellow Travertine", "YES109 Beige Coffee Travertine", "YES110 Roman Yellow Travertine", "YES111 Roman Travertine", "YES112 Red Travertine", "YES113 Imitation Surface Yellow Travertine", "YES114 Beige Grey Travertine", "YES115 Roman Beige Grey Travertine", "YES116 Roman Travertine", "YES117 Golden Travertine", "YES118 Yellow White Real Travertine", "YES119 Cloud White Travertine", "YES120 Light Yellow Travertine", "YES121 Beige White Travertine", "YES122 Yellow Roman Travertine", "YES123 Roman Grey-White Travertine", "YES124 Earth Yellow Travertine", "YES125 Roman Grey Travertine", "YES126 Roman Cloud White Travertine", "YES127 Italian Ivory White Travertine", "YES128 Roman White Travertine", "YES129 White Travertine", "YES130 Beige Grey Travertine", "YES131 Light Grey Travertine", "YES132 Roman Light Yellow Travertine", "YES133 Italian Yellow Travertine", "YES134 Italian White Travertine", "YES135 Beige Yellow Travertine", "YES136 Beige Yellow Travertine", "YES137 Beige Grey Travertine", "YES138 Silver Grey Travertine", "YES139 Cloud Grey Travertine", "YES140 Roman Travertine", "YES141 Cloud Grey Travertine", "YES142 Beige Grey Travertine", "YES143 Beige Grey Travertine", "YES144 Light Beige Yellow Travertine", "YES145 Roman Grey-White Travertine", "YES146 Italian Beige Travertine", "YES147 Light Yellow Travertine", "YES148 Italian Yellow Travertine", "YES149 Italian Grey Travertine", "YES150 Italian Beige Yellow Travertine", "YES151 Roman Travertine", "YES152 Beige Yellow Travertine", "YES153 Cloud Mountain Yellow Grey Travertine", "YES154 Roman Yellow Travertine", "YES155 Beige Yellow Travertine", "YES156 Roman Travertine", "YES157 Italian Red Travertine", "YES158 Beige Yellow Travertine", "YES159 Roman Grey Travertine", "YES160 Roman Yellow Travertine", "YES161 Roman White Travertine", "YES162 Roman Travertine", "YES163 Roman Coffee Travertine", "YES164 Roman Beige Yellow Travertine", "YES165 Italian White Travertine", "YES166 Italian Red Travertine", "CZMB654 Grey Travertine"] },
    { heading: "Slate, Dacite and Star Moon Stone", bullets: ["Catalogue 7 Slate: 600x1200 mm, 1200x3000 mm; thickness 2-3.5 mm. Example colours: Pure White, Beige yellow, Red, Light grey, Dark grey, Black.", "Catalogue 11 Star Moon Stone: 600x1200 mm, 1200x3000 mm; thickness 3-6 mm. Example colours: Beige White, Beige yellow, Black.", "Catalogue 15 Dacite: 600x1200 mm, 1200x3000 mm; thickness 3.5-4 mm. Example colours: Pure White, Beige yellow, Dark grey.", "Catalogue 10 Slate-look samples: legible labels BY-609, BY-604, plus descriptive stone-look names in the source. Enquire by photo."] },
    { heading: "Textured and Patterned Veneer", bullets: ["Catalogue 17 Ripple Board: 1200x600 mm, 1200x2840 mm; thickness 2-9 mm. Example colours: Pure White, Beige yellow, Dark grey.", "Catalogue 23 Round Line Stone: 1200x3000 mm; thickness 4-11 mm. Example colours: Beige White, Khaki, Black.", "Catalogue 29 Ridged Stone: 600x1200 mm, 1200x3000 mm; thickness 3-7 mm. Example colours: Beige yellow, Beige White, Light grey, Red.", "Catalogue 37 Muretto Stone: 600x1200 mm; thickness 2-6 mm. Example colours: Dark grey, Beige yellow, Khaki, Light grey, Black.", "Catalogue 55 W Weaving: 600x1200 mm, 1200x3000 mm; thickness 5 mm. Example colours: Beige yellow, Khaki, Dark grey.", "Catalogue 57 Lumpy Weave: 600x1200 mm; thickness 2-3.5 mm. Example colours: Beige yellow, Light grey, Yellow.", "Catalogue 74 Strip Stone: 600x1200 mm; thickness 3-6 mm. Example colours: Khaki, Light grey, Dark grey.", "Catalogue 83 Molog: 600x1200 mm; thickness 4-5 mm. Example colours: Beige White, Khaki, Light grey, Dark grey.", "Catalogue 89 Misaligned Tile Pattern: 1200x600 mm; thickness 4.5-5 mm. Example colours: Khaki, Light grey, Red, Dark grey.", "Catalogue 91 Firecracker Pattern: 600x1200 mm; thickness 3-3.5 mm. Example colours: Beige yellow, Light grey, Khaki, Red.", "Catalogue 94 Semi-circular Pebbles: 580x1180 mm; thickness 2-6 mm. Example colours: Beige White, Light grey, Dark grey.", "Catalogue 95 Herringbone: 600x1200 mm; thickness 2.5-3 mm. Example colours: Beige White, Beige yellow, Black.", "Catalogue 97 3D Fabric Stone: 600x1200 mm; thickness 2-4.5 mm. Example colours: Beige yellow, Khaki, Dark grey.", "Catalogue 99 Wave Stone: 600x1200 mm; thickness 3-4.5 mm. Example colours: Pure White, Beige yellow, Red, Light grey, Dark grey, Black.", "Catalogue 102 Banana Leaves: 600x1200 mm; thickness 1.5-5.2 mm. Example colours: Beige White, Light grey, Black.", "Installed-only sheets in these files, with no sample sheet or specification legible — enquire by photo: Catalogue 13-14, 21-22, 61-62, 73, 87, 93, 101."] },
    { heading: "Mosaic", bullets: ["Catalogue 25 Mosaic: 1200x600 mm, 1200x3000 mm; thickness 4-8 mm. Example colours: Light grey, Pure White, Beige yellow, Dark grey, Red.", "Catalogue 88 Piece Mosaic: 600x1200 mm; thickness 3.5-4 mm. Example colours: Pure White, Beige yellow, Light grey, Dark grey."] },
    { heading: "Wood Grain", bullets: ["Catalogue 46 Pine Wood Grain: 600x1200 mm; thickness 2.5-4 mm. Example colours: Beige yellow, Light grey, Black.", "Catalogue 47-48 Wood Grain: descriptive wood names only in the source, no legible model codes or specifications — enquire by photo.", "Catalogue 71 Wood Grain: 1150x3000 mm; thickness 3-4 mm. Example colours: Beige White, Khaki, Light grey, Dark grey, Medium grey."] },
    { heading: "Concrete Board", bullets: ["Catalogue 59 Concrete Board: 1200x3000 mm; thickness 3-5 mm. Example colours: Khaki, Dark grey, Light grey."] },
    { heading: "Flexible Brick Veneer", bullets: ["Catalogue 103-104 Brick: legible codes W-20011, W-20012, W-20015, W-20018, W-20021, W-20024; A-40011 to A-40018; P-30011 to P-30014. Size and thickness not legible on this sheet — confirm in enquiry.", "Catalogue 105-106 Brick: physical display and installed examples, no codes or specifications legible — enquire by photo."] },
    { heading: "Installation and Substrates — guidance only, not products", paragraphs: ["The source files include substrate and installation pages (Catalogue 107-110: cement, keel, white wall, concave brick, OSB board and metal bases, with installation steps and corner / joint treatment). These are shown as installation guidance only. Adhesive, fixing method, substrate preparation and joint treatment must be confirmed for your project in enquiry."] },
    { heading: "Performance", paragraphs: ["Material: modified clay (MCM). Fire performance: SGS fire rating Class A. No warranty period, lifetime claim or 30-year service-life claim is stated here for this new product range — please confirm warranty terms in your enquiry. Freeze-thaw performance and waterproofing: please confirm in your enquiry. This range is a flexible veneer with a stone look; it is not presented here as natural stone."] },
  ],
  orderingNote: "For flexible veneer, please confirm the minimum order in your enquiry.",
  enquiry: ["Company name and country / state / province", "Catalogue number / model code, if known: flex catalogue number, DS code, YES code, W / A / P code, or BY code, and a reference photo", "Application: indoor wall / outdoor wall", "Size and thickness required", "Quantity (pieces / square metres / containers)", "Delivery location / destination port and desired delivery time"],
};
